import type { ResourceKinds } from 'v5/core/entity-values/values.types';
import type { CustomFieldGroup, Resource, ResourceRate } from '__types__';
import {
  EditUnit,
  EntitySchema,
  FieldSpec,
  fieldSpecUtils,
  FieldsUnit,
  ListUnit
} from 'v5/core/entity-schema/entitySchema.types';
import {
  parseRateCardOptionId,
  rateCardOptionId
} from 'v5/core/entity-schema/resource/rateListOptions';
import { rateCardFormValue } from 'v5/core/entity-values/codecs/rate.codec';
import { compareGroupRows } from 'v5/core/entity-values/codecs/customFieldRows';
import {
  agentDetailsOf,
  isAgentRef,
  unitValuesAfterAgentPick
} from 'v5/core/entity-values/codecs/entity-ref.agent';
import { encodeList, encodeUnit } from 'v5/core/entity-values/encodeUnit';
import {
  CodecContext,
  CodecRegistry,
  ListItemValues,
  RowTarget,
  UnitValues
} from 'v5/core/entity-values/values.types';
import { RowId, rowIdUtils } from 'v5/core/form-schema/formRows';
import type { ResourcePatch } from 'v5/core/entity-values/resourcePatch.types';
import type { ResourceWriteModel } from 'v5/core/resource-write/ResourceWriteModel';
import type {
  ClearAction,
  FieldsUnitBinding,
  ListUnitBinding,
  UnitBindings
} from 'v5/patterns/forms/schema-form/schemaForm.types';

export type ResourceBindingsInput = {
  schema: EntitySchema;
  resource: Resource;
  codecs: CodecRegistry;
  writeModel: ResourceWriteModel;
  /** Another resource (a picked agent, whose details the save copies). */
  readResource(id: number): Promise<Resource>;
  /** workspaceId → workspace name, for "Overridden in …" badges. */
  workspaceNames: ReadonlyMap<number, string>;
  /** workspaceId → definition ids overridden there (from `collectOverrides`). */
  overrides: ReadonlyMap<number, ReadonlySet<number>>;
};

/** Rates in the order codecs index them: `order` ascending (nulls last), then id. */
export function orderedRates(resource: Resource): ResourceRate[] {
  return [...(resource.rates ?? [])].sort((a, b) => {
    const ao = a.order ?? Number.POSITIVE_INFINITY;
    const bo = b.order ?? Number.POSITIVE_INFINITY;
    return ao - bo || a.id - b.id;
  });
}

/** Live group rows of one definition, in the codecs' index order. */
export function orderedGroupRows(
  resource: Resource,
  definitionId: number
): CustomFieldGroup[] {
  return (resource.custom_fieldgroups ?? [])
    .filter(
      group =>
        group.archived !== true &&
        group.custom_fieldgroup_definition_id === definitionId
    )
    .sort(compareGroupRows);
}

/** Where a list's entry `index` lives: a group row, or a row of a relation of the entity. */
function listItemTarget(unit: ListUnit, index: number): RowTarget {
  if (unit.container.type === 'custom-field-group') {
    return {
      type: 'custom-field-group-row',
      definition: unit.container.definition,
      index
    };
  }
  return unit.item.fields[0]?.kind === 'location'
    ? { type: 'locations-item', index }
    : { type: 'rates-item', index };
}

export function decodeUnitValues(
  fields: FieldSpec[],
  resource: Resource,
  codecs: CodecRegistry,
  ctx: CodecContext
): UnitValues {
  const values: UnitValues = {};
  for (const field of fields) {
    values[field.key] = decodeField(field, resource, codecs, ctx);
  }
  return values;
}

function decodeField(
  field: FieldSpec,
  resource: Resource,
  codecs: CodecRegistry,
  ctx: CodecContext
) {
  switch (field.kind) {
    case 'text':
      return codecs.text.decode(resource, field, ctx);
    case 'boolean':
      return codecs.boolean.decode(resource, field, ctx);
    case 'number':
      return codecs.number.decode(resource, field, ctx);
    case 'date':
      return codecs.date.decode(resource, field, ctx);
    case 'date-range':
      return codecs['date-range'].decode(resource, field, ctx);
    case 'single-select':
      return codecs['single-select'].decode(resource, field, ctx);
    case 'multi-select':
      return codecs['multi-select'].decode(resource, field, ctx);
    case 'measurement':
      return codecs.measurement.decode(resource, field, ctx);
    case 'currency':
      return codecs.currency.decode(resource, field, ctx);
    case 'url':
      return codecs.url.decode(resource, field, ctx);
    case 'percentage':
      return codecs.percentage.decode(resource, field, ctx);
    case 'height':
      return codecs.height.decode(resource, field, ctx);
    case 'dimension':
      return codecs.dimension.decode(resource, field, ctx);
    case 'measurement-legacy':
      return codecs['measurement-legacy'].decode(resource, field, ctx);
    case 'netsuite-master-project':
      return codecs['netsuite-master-project'].decode(resource, field, ctx);
    case 'name':
      return codecs.name.decode(resource, field, ctx);
    case 'pronouns':
      return codecs.pronouns.decode(resource, field, ctx);
    case 'contact-details':
      return codecs['contact-details'].decode(resource, field, ctx);
    case 'tags':
      return codecs.tags.decode(resource, field, ctx);
    case 'groups':
      return codecs.groups.decode(resource, field, ctx);
    case 'location':
      return codecs.location.decode(resource, field, ctx);
    case 'rate':
      return codecs.rate.decode(resource, field, ctx);
    case 'entity-ref':
      return codecs['entity-ref'].decode(resource, field, ctx);
  }
}

export function emptyUnitValues(
  fields: FieldSpec[],
  codecs: CodecRegistry
): UnitValues {
  const values: UnitValues = {};
  for (const field of fields) {
    values[field.key] = emptyField(field, codecs);
  }
  return values;
}

function emptyField(field: FieldSpec, codecs: CodecRegistry) {
  switch (field.kind) {
    case 'text':
      return codecs.text.empty(field);
    case 'boolean':
      return codecs.boolean.empty(field);
    case 'number':
      return codecs.number.empty(field);
    case 'date':
      return codecs.date.empty(field);
    case 'date-range':
      return codecs['date-range'].empty(field);
    case 'single-select':
      return codecs['single-select'].empty(field);
    case 'multi-select':
      return codecs['multi-select'].empty(field);
    case 'measurement':
      return codecs.measurement.empty(field);
    case 'currency':
      return codecs.currency.empty(field);
    case 'url':
      return codecs.url.empty(field);
    case 'percentage':
      return codecs.percentage.empty(field);
    case 'height':
      return codecs.height.empty(field);
    case 'dimension':
      return codecs.dimension.empty(field);
    case 'measurement-legacy':
      return codecs['measurement-legacy'].empty(field);
    case 'netsuite-master-project':
      return codecs['netsuite-master-project'].empty(field);
    case 'name':
      return codecs.name.empty(field);
    case 'pronouns':
      return codecs.pronouns.empty(field);
    case 'contact-details':
      return codecs['contact-details'].empty(field);
    case 'tags':
      return codecs.tags.empty(field);
    case 'groups':
      return codecs.groups.empty(field);
    case 'location':
      return codecs.location.empty(field);
    case 'rate':
      return codecs.rate.empty(field);
    case 'entity-ref':
      return codecs['entity-ref'].empty(field);
  }
}

/**
 * Values for a new list row. `optionId` comes from the list's add menu:
 * a rate-card option pre-fills a rate linked to that card; anything else
 * (or `null`) yields the codecs' empty values.
 */
export function newListItemValues(
  unit: ListUnit,
  optionId: string | null,
  codecs: CodecRegistry
): UnitValues {
  const rateField = unit.item.fields.find(field => field.kind === 'rate');
  const cardId = optionId === null ? null : parseRateCardOptionId(optionId);
  if (rateField && rateField.kind === 'rate' && cardId !== null) {
    const card = rateField.rateCards.find(candidate => candidate.id === cardId);
    if (card) {
      return { [rateField.key]: rateCardFormValue(card) };
    }
  }
  return emptyUnitValues(unit.item.fields, codecs);
}

/** Rate card linked by a rates-list row, `null` for custom rates and non-rate lists. */
function linkedRateCardId(unit: ListUnit, values: UnitValues): number | null {
  const rateField = unit.item.fields.find(field => field.kind === 'rate');
  if (!rateField) return null;
  const value = values[rateField.key];
  if (
    typeof value === 'object' &&
    value !== null &&
    'rate_id' in value &&
    typeof value.rate_id === 'number'
  ) {
    return value.rate_id;
  }
  return null;
}

function listRowIds(unit: ListUnit, resource: Resource): RowId[] {
  if (unit.container.type === 'custom-field-group') {
    return orderedGroupRows(resource, unit.container.definition.id).map(row =>
      rowIdUtils.persisted(row.id)
    );
  }
  if (unit.item.fields[0]?.kind === 'location') {
    return (resource.locations ?? []).map(location =>
      rowIdUtils.persisted(location.id)
    );
  }
  return orderedRates(resource).map(rate => rowIdUtils.persisted(rate.id));
}

function unitContext(unit: FieldsUnit): CodecContext {
  return {
    scope: unit.scope,
    target:
      unit.container.type === 'custom-field-group'
        ? {
            type: 'custom-field-group-row',
            definition: unit.container.definition,
            index: 0
          }
        : { type: 'entity' }
  };
}

/** The agent newly referenced by the unit's values, `null` when it holds no new one. */
function pickedAgentId(
  unit: FieldsUnit,
  values: UnitValues,
  resource: Resource
): number | null {
  for (const field of unit.fields) {
    if (field.kind !== 'entity-ref' || !isAgentRef(field)) continue;
    const id = values[field.key];
    if (typeof id === 'number' && id !== (resource.represented_by_id ?? null)) {
      return id;
    }
  }
  return null;
}

/**
 * Saves a unit that newly references an agent, copying the agent's details
 * into the units the form shows (v1 `RESOURCE_CONNECT`): other core columns
 * (the organisation) join the unit's own core write, each other relation
 * (emails, phone numbers) is a write of its own, encoded against the
 * resource the previous write returned.
 */
async function saveWithAgentDetails(
  unit: FieldsUnit,
  patch: ResourcePatch,
  agentId: number,
  input: ResourceBindingsInput
): Promise<void> {
  const { schema, resource, codecs, writeModel } = input;
  const agent = agentDetailsOf(await input.readResource(agentId));
  const others = schema.sections
    .flatMap(section => section.units)
    .filter(
      (other): other is FieldsUnit =>
        other.type === 'fields' && other.id !== unit.id
    );
  const valuesAfterPick = (other: FieldsUnit, from: Resource) =>
    unitValuesAfterAgentPick(
      other.fields,
      decodeUnitValues(other.fields, from, codecs, unitContext(other)),
      agent
    );

  if (patch.kind !== 'core') {
    throw new Error(`Unit "${unit.id}" holds the agent but is no core write`);
  }
  const coreChanges = [...patch.changes];
  const relations: FieldsUnit[] = [];
  for (const other of others) {
    const values = valuesAfterPick(other, resource);
    if (values === null) continue;
    const otherPatch = encodeUnit(other, values, resource, codecs);
    if (otherPatch.kind === 'core') {
      coreChanges.push(...otherPatch.changes);
    } else {
      relations.push(other);
    }
  }

  let current = await writeModel.apply(resource.id, {
    kind: 'core',
    changes: coreChanges
  });
  for (const relation of relations) {
    const values = valuesAfterPick(relation, current);
    if (values === null) continue;
    current = await writeModel.apply(
      resource.id,
      encodeUnit(relation, values, current, codecs)
    );
  }
}

function buildFieldsBinding(
  unit: FieldsUnit,
  input: ResourceBindingsInput
): FieldsUnitBinding<ResourceKinds> {
  const { resource, codecs, writeModel } = input;
  const ctx = unitContext(unit);
  const definition =
    unit.container.type === 'entity' && unit.fields.length === 1
      ? fieldSpecUtils.definitionOf(unit.fields[0])
      : null;

  const clear: ClearAction =
    unit.origin.type === 'mirror' &&
    unit.inheritance.type === 'overridden' &&
    definition !== null
      ? {
          type: 'available',
          run: async () => {
            await writeModel.apply(resource.id, {
              kind: 'custom-field',
              definitionId: definition.id,
              scope: unit.scope,
              write: { type: 'clear' }
            });
          }
        }
      : { type: 'not-applicable' };

  const overriddenIn: string[] = [];
  if (unit.origin.type === 'global' && definition !== null) {
    for (const [workspaceId, definitionIds] of input.overrides) {
      if (definitionIds.has(definition.id)) {
        overriddenIn.push(
          input.workspaceNames.get(workspaceId) ?? `Workspace ${workspaceId}`
        );
      }
    }
  }

  return {
    type: 'fields',
    values: decodeUnitValues(unit.fields, resource, codecs, ctx),
    save: async values => {
      const patch = encodeUnit(unit, values, resource, codecs);
      const agentId = pickedAgentId(unit, values, resource);
      if (agentId === null) {
        await writeModel.apply(resource.id, patch);
        return;
      }
      await saveWithAgentDetails(unit, patch, agentId, input);
    },
    clear,
    overriddenIn
  };
}

function buildListBinding(
  unit: ListUnit,
  input: ResourceBindingsInput
): ListUnitBinding<ResourceKinds> {
  const { resource, codecs, writeModel } = input;
  const items: ListItemValues[] = listRowIds(unit, resource).map(
    (rowId, index) => ({
      rowId,
      values: decodeUnitValues(unit.item.fields, resource, codecs, {
        scope: unit.scope,
        target: listItemTarget(unit, index)
      })
    })
  );

  const applyList = async (next: ListItemValues[]) => {
    await writeModel.apply(
      resource.id,
      encodeList(unit, next, resource, codecs)
    );
  };

  return {
    type: 'list',
    items,
    saveItems: applyList,
    newItem: optionId => newListItemValues(unit, optionId, codecs),
    usedAddOptions: rows =>
      rows
        .map(values => linkedRateCardId(unit, values))
        .filter((id): id is number => id !== null)
        .map(rateCardOptionId)
  };
}

/** One binding per unit of the schema, all reading from the same resource snapshot. */
export function buildResourceBindings(
  input: ResourceBindingsInput
): UnitBindings<ResourceKinds> {
  const bindings: UnitBindings<ResourceKinds> = {};
  for (const section of input.schema.sections) {
    for (const unit of section.units) {
      bindings[unit.id] = buildUnitBinding(unit, input);
    }
  }
  return bindings;
}

function buildUnitBinding(unit: EditUnit, input: ResourceBindingsInput) {
  return unit.type === 'list'
    ? buildListBinding(unit, input)
    : buildFieldsBinding(unit, input);
}
